import React, { useEffect, useRef } from 'react';
import { Users, Package, Handshake } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const AboutSection = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      const cards = sectionRef.current.querySelectorAll('.feature-card');
      cards.forEach((card) => observer.observe(card));
    }

    return () => observer.disconnect();
  }, []);

  const features = [
    {
      icon: Users,
      title: 'Reuniones uno a uno',
      description: 'Encuentros directos con representantes y ejecutivos de las principales marcas.',
      color: 'from-orange-500 to-pink-500'
    },
    {
      icon: Package,
      title: 'Espacios de exhibición',
      description: 'Conocimiento de productos, novedades y lanzamientos.',
      color: 'from-pink-500 to-orange-500'
    },
    {
      icon: Handshake,
      title: 'Networking estratégico',
      description: 'Generación de contactos y oportunidades de negocio.',
      color: 'from-orange-600 to-pink-600'
    }
  ];

  return (
    <section className="py-24 bg-slate-900 relative overflow-hidden">
      {/* Parallax background */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-800/50 to-transparent"></div>
      
      <div ref={sectionRef} className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            ¿Qué es Elit Brand Tour?
          </h2>
          <p className="text-lg text-slate-300 max-w-4xl mx-auto leading-relaxed">
            Elit Brand Tour es un espacio de alta productividad diseñado para generar oportunidades reales de negocio.
          </p>
        </div>

        <div className="mb-16 max-w-4xl mx-auto">
          <p className="text-lg text-slate-300 leading-relaxed text-center">
            Durante una jornada intensiva, los asistentes participan de reuniones uno a uno con referentes
            de las principales marcas de tecnología, recorren espacios de exhibición de productos y acceden
            a áreas especialmente diseñadas para networking profesional.
          </p>
          <p className="text-lg text-slate-300 leading-relaxed text-center mt-6">
            Todas las marcas que hacen competitivo al canal reunidas en un mismo lugar para conectar,
            descubrir tendencias y generar nuevas oportunidades comerciales.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <Card
                key={index}
                className="feature-card opacity-0 bg-slate-800 border-slate-700 hover:border-orange-500 transition-all duration-300 hover:shadow-xl hover:-translate-y-2"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <CardContent className="p-8 text-center">
                  <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${feature.color} rounded-full mb-6 shadow-lg`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-slate-300 leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
